import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Paginație pentru listele de administrare.
 *
 * Listele astea sunt deja aduse întregi de la server și filtrate în client,
 * așa că paginarea e tot în client: tăiem felia vizibilă cu `usePagination`.
 * Markup-ul și cheile de traducere sunt cele folosite deja în
 * UserManagement, ca să nu apară un al doilea stil de paginare în aplicație.
 */

export const DEFAULT_PAGE_SIZE = 25;

type Props = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
};

export default function Pagination({ page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
      <div className="text-sm text-slate-500">
        {t("common.showing", "Se afișează")} {first} - {last} {t("common.of", "din")} {total}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200"
        >
          {t("common.previous", "Anterior")}
        </button>
        <span className="px-4 py-2 text-slate-600">
          {t("common.page", "Pagina")} {page} {t("common.of", "din")} {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200"
        >
          {t("common.next", "Următor")}
        </button>
      </div>
    </div>
  );
}

/**
 * Felia vizibilă dintr-o listă deja filtrată.
 *
 * Revine pe pagina 1 când filtrele schimbă lungimea listei, altfel un filtru
 * aplicat de pe pagina 4 ar lăsa tabelul gol, iar utilizatorul ar crede că nu
 * există rezultate.
 */
export function usePagination<T>(items: T[], pageSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = React.useState(1);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  React.useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);
  const visible = React.useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize]
  );

  return { page: safePage, setPage, pageSize, total, visible };
}
